import { useEffect, useState } from 'react';
import { useStore } from '@/store/useStore';
import { ArrowLeft, Timer, Loader2, ShieldAlert } from 'lucide-react';
import { canManageBranches } from '@/auth/permissions';
import { getSettings, savePrebookingSettings } from '@/auth/adminClient';

// Дефолты (в минутах) — совпадают с серверными в sync.ts.
const B2C_DEFAULT_MIN = 120;   // 2 ч
const B2B_DEFAULT_MIN = 4320;  // 72 ч

/**
 * Экран «Настройки предброни» — только руководитель сети и супер-админ.
 * Действует на весь портал: пишем в наш синглтон настроек (в Битрикс ничего не уходит).
 * - Рубильник авто-аннулирования (prebooking_enabled): выкл → слот не слетает.
 * - Сроки удержания для обычного клиента и для компании (часы → минуты при сохранении).
 */
export function PrebookingSettingsPage() {
  const setPage = useStore((s) => s.setPage);
  const currentUser = useStore((s) => s.currentUser);
  const toast = useStore((s) => s.toast);
  const canManage = canManageBranches(currentUser);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [b2cHours, setB2cHours] = useState(B2C_DEFAULT_MIN / 60);
  const [b2bHours, setB2bHours] = useState(B2B_DEFAULT_MIN / 60);

  useEffect(() => {
    if (!canManage) { setLoading(false); return; }
    (async () => {
      try {
        const s: any = (await getSettings()) ?? {};
        setEnabled(s.prebooking_enabled !== false); // нет ключа → включено
        // Обратная совместимость: если нового ключа _b2c нет, берём старый общий prebooking_hold_minutes.
        const b2cMin = Number(s.prebooking_hold_minutes_b2c ?? s.prebooking_hold_minutes);
        setB2cHours(Number.isFinite(b2cMin) && b2cMin > 0 ? b2cMin / 60 : B2C_DEFAULT_MIN / 60);
        const b2bMin = Number(s.prebooking_hold_minutes_b2b);
        setB2bHours(Number.isFinite(b2bMin) && b2bMin > 0 ? b2bMin / 60 : B2B_DEFAULT_MIN / 60);
      } catch (e) {
        toast({ kind: 'error', text: e instanceof Error ? e.message : 'Не удалось загрузить настройки' });
      } finally {
        setLoading(false);
      }
    })();
  }, [canManage]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await savePrebookingSettings({
        prebooking_enabled: enabled,
        prebooking_hold_minutes_b2c: Math.max(1, Math.round(b2cHours * 60)),
        prebooking_hold_minutes_b2b: Math.max(1, Math.round(b2bHours * 60)),
      });
      toast({ kind: 'success', text: 'Настройки предброни сохранены' });
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Ошибка сохранения' });
    } finally {
      setSaving(false);
    }
  };

  // Гейт по роли — реальная защита на сервере (canManageBranches), тут просто заглушка.
  if (!canManage) {
    return (
      <div className="h-full grid place-items-center bg-surface-0 text-ink-2 p-6 text-center">
        <div className="space-y-3 max-w-sm">
          <ShieldAlert className="w-8 h-8 text-ink-3 mx-auto" />
          <div className="text-lg font-semibold text-ink-1">Доступ только у руководителя</div>
          <p className="text-sm">Эти настройки меняет руководитель сети или супер-админ.</p>
          <button className="btn-soft mx-auto" onClick={() => setPage('calendar')}>Назад</button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-surface-0">
      <div className="min-h-12 py-1.5 px-4 flex items-center gap-3 border-b border-line bg-surface-1 flex-shrink-0">
        <button className="icon-btn" onClick={() => setPage('calendar')} title="Назад">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-base font-semibold flex items-center gap-2 min-w-0">
          <Timer className="w-4 h-4 text-accent flex-shrink-0" />
          <span className="truncate">Настройки предброни</span>
        </h1>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="text-ink-3 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Загрузка…</div>
        ) : (
          <div className="max-w-2xl space-y-4">
            <div className="panel p-4 space-y-3">
              <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-accent mt-0.5"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                <span>
                  <span className="font-semibold">Авто-аннулирование предброни</span>
                  <p className="text-[11px] text-ink-3 mt-0.5 leading-relaxed">
                    Если предоплата не пришла в срок — предбронь сгорает и освобождает слот.
                    Выключите, чтобы слот держался, пока предбронь не снимут вручную.
                  </p>
                </span>
              </label>
            </div>

            <div className="panel p-4 space-y-3">
              <h3 className="text-sm font-semibold">Сколько держать слот до предоплаты</h3>
              <div>
                <label className="field-label">Срок для обычного клиента, часы</label>
                <input
                  type="number"
                  min={1}
                  max={720}
                  className="input w-32"
                  value={b2cHours}
                  onChange={(e) => setB2cHours(Math.max(1, parseFloat(e.target.value) || B2C_DEFAULT_MIN / 60))}
                />
                <p className="text-[11px] text-ink-3 mt-1">Сколько держать предбронь, пока не пришла предоплата. По умолчанию 2 ч.</p>
              </div>
              <div>
                <label className="field-label">Срок для компании, часы</label>
                <input
                  type="number"
                  min={1}
                  max={2160}
                  className="input w-32"
                  value={b2bHours}
                  onChange={(e) => setB2bHours(Math.max(1, parseFloat(e.target.value) || B2B_DEFAULT_MIN / 60))}
                />
                <p className="text-[11px] text-ink-3 mt-1">72 ч = 3 дня. По умолчанию 72 ч.</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button className="btn-primary" onClick={save} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Сохранить
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
